import { describeMutationError } from "@api/mutationErrors";
import { useWriteErrorStore } from "@api/writeErrorStore";
import { useClinic } from "@clinic/clinicQueries";
import { viewCaption, type ViewIdentity } from "@views/viewIdentity";
import html2canvas from "html2canvas-pro";
import { jsPDF } from "jspdf";
import { useCallback, useRef, useState } from "react";

const sanitizeFilename = (name: string) =>
  name
    .trim()
    .replace(/[^\w-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "view";

type Rgb = [number, number, number];

const MAX_CANVAS_DIM = 16384;
const MAX_CANVAS_AREA = MAX_CANVAS_DIM ** 2;

// Content is laid out at this fixed width in the capture clone so exports look
// the same regardless of the user's window size.
const EXPORT_WIDTH = 1024;

const fittedScale = (width: number, height: number): number =>
  Math.min(
    2,
    MAX_CANVAS_DIM / width,
    MAX_CANVAS_DIM / height,
    Math.sqrt(MAX_CANVAS_AREA / (width * height)),
  );

const parseRgb = (color: string): Rgb | null => {
  const match = color.match(/rgba?\(([^)]+)\)/);
  if (!match) return null;
  const [r, g, b, a] = match[1].split(",").map((n) => parseFloat(n));
  if (a === 0) return null;
  return [r, g, b];
};

/*
 * A PDF is print output, not a themed screen, so the capture flattens to plain
 * white rather than reproducing the tinted paper (cream card stock, recess
 * wells) or whatever surface the sheet happens to be lying on. The hex is the
 * one deliberate literal in the app: print white, verified against
 * html2canvas-pro 2.x's `backgroundColor: string | null` option.
 *
 * The paper tokens are re-pointed at white on the capture clone, so everything
 * inside that is "paper" (bg-card, bg-paper, bg-recess) prints as the page
 * itself while the status washes and inks - which carry meaning - keep their
 * color at the ink's own strength.
 */
const PRINT_WHITE = "#ffffff";
const PAPER_TOKENS = ["--color-card", "--color-paper", "--color-recess"];

type Logo = { dataUrl: string; width: number; height: number };

/**
 * Fetches the clinic logo and rasterises it to a PNG data URL via a canvas, so
 * any allowed source format (svg/webp/jpeg/png) feeds `jsPDF.addImage` uniformly.
 * Returns null on any failure — the export proceeds without a logo rather than
 * aborting (CORS, a stale presigned URL, an unreadable image, …).
 */
const loadLogo = async (url: string): Promise<Logo | null> => {
  let objectUrl: string | null = null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    objectUrl = URL.createObjectURL(blob);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = objectUrl!;
    });
    const width = img.naturalWidth || 200;
    const height = img.naturalHeight || 200;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, width, height);
    return { dataUrl: canvas.toDataURL("image/png"), width, height };
  } catch (err) {
    console.error("Clinic logo load failed; exporting without it:", err);
    return null;
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
};

interface Branding {
  name: string | null;
  address: string | null;
  logo: Logo | null;
}

/**
 * Exports a SavedView's rendered a2ui tree to PDF by rasterising the DOM `ref`
 * points at, then slicing the capture across letter pages. Working at the
 * rendered-DOM layer means every current and future a2ui component gets PDF for
 * free — there is no per-component export code to keep in sync with `UiRenderer`.
 * `html2canvas-pro` over `html2canvas` because the design system's Tailwind v4
 * palette emits `oklch()` colours.
 *
 * `onclone` restyles html2canvas's clone before it measures crop bounds: `dark` is
 * stripped so PDFs are always light, the node is forced to `EXPORT_WIDTH` so layout
 * doesn't depend on window size, ancestor overflow is made visible so the full
 * content renders rather than the scroll-clipped viewport, and the paper tokens are
 * flattened to `PRINT_WHITE` so the export is a plain white page rather than a
 * photograph of tinted card stock. Decorative chrome opts out of the capture with
 * `data-html2canvas-ignore` (the estimate's carbon copies); a signed view's stamp
 * and sign-off trail stay in - they are part of the record.
 *
 * Clinic branding (WEB-3184) is drawn as PDF chrome so an exported record looks
 * like the clinic's, not Scribenote's.
 */
export const useViewPdfExport = (view: ViewIdentity) => {
  const ref = useRef<HTMLDivElement>(null);
  const exportingRef = useRef(false);
  const [exporting, setExporting] = useState(false);
  const { data: clinic } = useClinic();
  // Resolved out here so the callback depends on a string rather than on
  // `view`, whose `subjects` array is a fresh object on every refetch.
  const headerCaption = viewCaption(view, { withSubject: true });

  const exportPdf = useCallback(async () => {
    const node = ref.current;
    if (!node || exportingRef.current) return;

    exportingRef.current = true;
    setExporting(true);
    try {
      await document.fonts.ready;
      // Reflowing to EXPORT_WIDTH changes the content height; this estimate
      // only feeds the canvas-size guard and the clone's viewport height, so
      // it errs tall rather than clipping.
      const estimatedHeight = Math.ceil(
        Math.max(1, node.scrollWidth / EXPORT_WIDTH) * node.scrollHeight,
      );
      let text: Rgb = [0, 0, 0];
      const canvas = await html2canvas(node, {
        scale: fittedScale(EXPORT_WIDTH, estimatedHeight),
        backgroundColor: PRINT_WHITE,
        windowWidth: EXPORT_WIDTH,
        windowHeight: estimatedHeight,
        onclone: (_doc, el) => {
          el.ownerDocument.documentElement.classList.remove("dark");
          el.style.width = `${EXPORT_WIDTH}px`;
          el.querySelectorAll("button, textarea").forEach((n) => n.remove());
          for (let a = el.parentElement; a; a = a.parentElement) {
            a.style.overflow = "visible";
          }
          // Flatten the sheet to print white: the custom properties inherit,
          // so every paper surface in the capture goes white while the ink
          // stays the ink. The PDF's own chrome text borrows the document's
          // resolved ink so header and content print in one color.
          for (const token of PAPER_TOKENS) {
            el.style.setProperty(token, PRINT_WHITE);
          }
          el.style.backgroundColor = PRINT_WHITE;
          const view = el.ownerDocument.defaultView ?? window;
          text = parseRgb(view.getComputedStyle(el).color) ?? [0, 0, 0];
        },
      });
      const [textR, textG, textB] = text;
      // An empty view renders a degenerate canvas; bail rather than write a
      // blank PDF. Says so rather than failing quietly: this branch is
      // deterministic, so a user left guessing would retry forever.
      if (canvas.width < 2 || canvas.height < 2) {
        useWriteErrorStore
          .getState()
          .show("This view has nothing to export yet.");
        return;
      }

      const branding: Branding = {
        name: clinic?.name ?? null,
        address: clinic?.address?.replace(/\s*\n\s*/g, ", ").trim() || null,
        logo: clinic?.logoUrl ? await loadLogo(clinic.logoUrl) : null,
      };

      const pdf = new jsPDF({ unit: "pt", format: "letter" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 36;
      const contentWidth = pageWidth - margin * 2;
      const pxPerPt = canvas.width / contentWidth;
      // Leaves room at the bottom of every page for the footer band.
      const bottomReserve = margin + 16;

      // Page-one branding header. Draws onto the current page and returns the y
      // at which sliced content should begin.
      const drawHeader = (): number => {
        const y = margin;
        let textX = margin;
        let headerBottom = y;

        if (branding.logo) {
          const logoH = 40;
          const logoW = Math.min(
            (branding.logo.width / branding.logo.height) * logoH,
            160,
          );
          pdf.addImage(branding.logo.dataUrl, "PNG", margin, y, logoW, logoH);
          textX = margin + logoW + 12;
          headerBottom = y + logoH;
        }

        let textY = y + 11;
        const textWidth = pageWidth - margin - textX;
        if (branding.name) {
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(13);
          pdf.setTextColor(textR, textG, textB);
          pdf.text(branding.name, textX, textY);
          textY += 15;
        }
        if (branding.address) {
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(9);
          pdf.setTextColor(textR, textG, textB);
          const lines = pdf.splitTextToSize(branding.address, textWidth);
          pdf.text(lines, textX, textY);
          textY += lines.length * 11;
        }
        headerBottom = Math.max(headerBottom, textY);

        const ruleY = headerBottom + 8;
        pdf.setDrawColor(textR, textG, textB);
        pdf.setLineWidth(1.5);
        pdf.line(margin, ruleY, pageWidth - margin, ruleY);

        const titleFontSize = 15;
        const titleLineHeight = 18;
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(titleFontSize);
        pdf.setTextColor(textR, textG, textB);
        const titleLines = pdf.splitTextToSize(view.name, contentWidth);
        const titleY = ruleY + 14 + titleFontSize;
        pdf.text(titleLines, margin, titleY, {
          lineHeightFactor: titleLineHeight / titleFontSize,
        });
        let bottom = titleY + (titleLines.length - 1) * titleLineHeight;

        // The exported record ALWAYS names the patient, whatever the screen it
        // was exported from decided (BE-2395): the title is only the kind of
        // document, and this file leaves the app - it is handed to a client,
        // faxed to a referral practice, or filed. A sheet that says "Medical
        // Note" and nothing else is not a record of anything.
        if (headerCaption) {
          const captionFontSize = 10;
          const captionLineHeight = 13;
          pdf.setFontSize(captionFontSize);
          const captionLines = pdf.splitTextToSize(headerCaption, contentWidth);
          const captionY = bottom + captionLineHeight;
          pdf.text(captionLines, margin, captionY, {
            lineHeightFactor: captionLineHeight / captionFontSize,
          });
          bottom = captionY + (captionLines.length - 1) * captionLineHeight;
        }
        return bottom + 12;
      };

      // No page fill: a jsPDF page is already the print white the capture
      // flattened to.
      const page0Top = drawHeader();

      let sourceY = 0;
      let pageIndex = 0;
      while (sourceY < canvas.height) {
        if (pageIndex > 0) pdf.addPage();
        const topOffset = pageIndex === 0 ? page0Top : margin;
        const usablePt = pageHeight - bottomReserve - topOffset;
        const slicePx = Math.min(
          Math.floor(usablePt * pxPerPt),
          canvas.height - sourceY,
        );
        // If the page-one header leaves no room, move content to a fresh page
        // rather than dropping it. A full page with no room (impossible unless
        // the margins exceed the page) breaks out instead of looping forever.
        if (slicePx <= 0) {
          if (pageIndex === 0) {
            pageIndex += 1;
            continue;
          }
          break;
        }

        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = slicePx;
        const ctx = slice.getContext("2d");
        if (!ctx) break;
        ctx.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          slicePx,
          0,
          0,
          canvas.width,
          slicePx,
        );

        pdf.addImage(
          slice.toDataURL("image/jpeg", 0.85),
          "JPEG",
          margin,
          topOffset,
          contentWidth,
          slicePx / pxPerPt,
        );

        sourceY += slicePx;
        pageIndex += 1;
      }

      const totalPages = pdf.getNumberOfPages();
      const footerY = pageHeight - margin + 4;
      for (let page = 1; page <= totalPages; page++) {
        pdf.setPage(page);
        pdf.setDrawColor(textR, textG, textB);
        pdf.setLineWidth(0.75);
        pdf.line(margin, footerY - 10, pageWidth - margin, footerY - 10);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(textR, textG, textB);
        if (branding.name) pdf.text(branding.name, margin, footerY);
        pdf.text(`Page ${page} of ${totalPages}`, pageWidth - margin, footerY, {
          align: "right",
        });
      }

      const date = new Date().toISOString().slice(0, 10);
      // The caption belongs in the filename as much as in the header: names
      // repeat by design now, so `view.name` alone gives a receptionist a
      // Downloads folder of `Medical-Note-2026-08-14.pdf`, `… (1)`, `… (2)`,
      // none of them telling which patient's record is which.
      pdf.save(
        `${sanitizeFilename([view.name, headerCaption].filter(Boolean).join(" "))}-${date}.pdf`,
      );
    } catch (err) {
      console.error("View PDF export failed:", err);
      // Export renders locally, so it never reaches the `MutationCache` floor —
      // but the user's click failed just the same, and the same notice is the
      // one place they look. Worded through `describeMutationError` so a failed
      // export doesn't read differently from a failed write.
      useWriteErrorStore
        .getState()
        .show(describeMutationError(err, "export the PDF"));
    } finally {
      exportingRef.current = false;
      setExporting(false);
    }
  }, [
    view.name,
    headerCaption,
    clinic?.name,
    clinic?.address,
    clinic?.logoUrl,
  ]);

  return { ref, exportPdf, exporting };
};
